import { useGetTagsQuery } from '@carboncopyco/data';
import type { TimePeriod, Workspace } from '@carboncopyco/types';
import { useDashboardAnalytics } from '@carboncopyco/utils';
import { useSet } from '@uidotdev/usehooks';
import constate from 'constate';
import { useMemo, useState } from 'react';
import { useUrlState } from '../utils';
import type { DashboardProps } from './Dashboard';

export type DashboardContextProps = {
  tab?: 'demo' | 'tour';
  workspaceIds?: string;
  tagIds?: string;
  timeRange?: TimePeriod;
  internal?: boolean;
  short?: boolean;
};

const initialUrlState: DashboardContextProps = {
  tab: 'demo',
  timeRange: '30',
};

const useDashboard = (props: DashboardProps) => {
  const { data: tags } = useGetTagsQuery({
    organizationId: props.organizationId,
  });
  const [urlState, setUrlState] =
    useUrlState<DashboardContextProps>(initialUrlState);
  const selectedTagIds = useSet<string>(urlState.tagIds?.split(','));
  const { tab, timeRange, workspaceIds } = urlState;
  const short = urlState.short === 'true';
  const internal = urlState.internal === 'true';
  const { trackDashboardWorkspaceFilterClick } = useDashboardAnalytics();

  const setTab = (tab: DashboardContextProps['tab']) =>
    setUrlState({ ...urlState, tab });

  const setShort = (isShortOn: DashboardContextProps['short']) =>
    setUrlState({ ...urlState, short: isShortOn });

  const setInternal = (isInternalOn: DashboardContextProps['internal']) =>
    setUrlState({ ...urlState, internal: isInternalOn });

  const setTimeRange = (timeRange: DashboardContextProps['timeRange']) =>
    setUrlState({ ...urlState, timeRange });

  const toggleTagId = (tagId: string) => {
    selectedTagIds.has(String(tagId))
      ? selectedTagIds.delete(String(tagId))
      : selectedTagIds.add(String(tagId));

    setUrlState({
      ...urlState,
      tagIds: [...selectedTagIds.values()].join(','),
    });
  };

  const clearTagIds = () => {
    selectedTagIds.clear();
    setUrlState({ ...urlState, tagIds: undefined });
  };

  const setWorkspaceIds = (workspaceId: Workspace['id']) => {
    const optionsSet = new Set<Workspace['id']>(
      workspaceIds?.split(',').map((option) => option.trim())
    );
    if (optionsSet.has(workspaceId)) {
      optionsSet.delete(workspaceId);
    } else {
      optionsSet.add(workspaceId);
    }
    const selectedWorkspaces = Array.from(optionsSet.values());

    setUrlState({
      ...urlState,
      workspaceIds: selectedWorkspaces.join(','),
    });

    trackDashboardWorkspaceFilterClick({
      dashboardWorkspaceFilter: selectedWorkspaces,
    });
  };

  const clearWorkspaceIds = () =>
    setUrlState({ ...urlState, workspaceIds: undefined });

  const [isScrolling, setIsScrolling] = useState(false);

  function handleScroll(event) {
    const { scrollTop } = event.target;
    setIsScrolling(scrollTop > 0);
  }

  const selectedWorkspaceIds = useMemo(() => {
    const workspacesList = new Set<string>(workspaceIds?.split(',') || []);

    selectedTagIds.forEach((tagId) => {
      const tagWorkspaceIds = tags?.find(
        (tag) => String(tag.id) === tagId
      )?.workspaceIds;
      if (tagWorkspaceIds) {
        tagWorkspaceIds.forEach((workspaceId) => {
          !workspacesList.has(String(workspaceId)) &&
            workspacesList.add(String(workspaceId));
        });
      }
    });
    return workspacesList.size > 0 ? [...workspacesList.values()] : undefined;
  }, [workspaceIds, selectedTagIds.size, tags]);

  return {
    tab,
    setTab,
    workspaceIds,
    setWorkspaceIds,
    clearWorkspaceIds,
    selectedTagIds,
    toggleTagId,
    clearTagIds,
    internal,
    setInternal,
    short,
    setShort,
    timeRange,
    setTimeRange,
    isScrolling,
    handleScroll,
    selectedWorkspaceIds,
    ...props,
  };
};

export const [DashboardProvider, useDashboardProps] = constate(useDashboard);
